import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Ship, FileSearch, Route, GraduationCap, Eye, ShieldCheck, Navigation } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { AllocationPanel } from "@/components/sc/AllocationPanel";
import { InvoiceAuditPanel } from "@/components/sc/InvoiceAuditPanel";
import { ServicesPanel } from "@/components/sc/ServicesPanel";
import { TrainingPanel } from "@/components/sc/TrainingPanel";
import { TrackingPanel } from "@/components/sc/TrackingPanel";

const TABS = [
  { id: "allocation", label: "Vessel Allocation", icon: Ship },
  { id: "tracking", label: "Container Tracking", icon: Navigation },
  { id: "invoice", label: "Invoice Audit", icon: FileSearch },
  { id: "services", label: "Services", icon: Route },
  { id: "training", label: "Training", icon: GraduationCap },
];

const INVOICE_VIEWERS = ["nicholasbooth@mondenissin.com.au"];
const TRACKING_EDITORS = ["nicholasbooth@mondenissin.com.au"];
const ALLOCATION_EDITORS = ["nicholasbooth@mondenissin.com.au"];

export default function SupplyChain() {
  const { user } = useAuth();

  const isAdmin = user?.role === "admin";
  const email = (user?.email || "").toLowerCase();
  const canInvoice = isAdmin || INVOICE_VIEWERS.includes(email);
  const canTrack = isAdmin || TRACKING_EDITORS.includes(email);
  const canAllocate = isAdmin || ALLOCATION_EDITORS.includes(email);

  const tabs = TABS; // all tabs viewable to everyone (read-only unless permitted)

  const [tab, setTab] = useState(null);
  const active = tab && tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id;

  const extra = [canAllocate && "Allocation", canInvoice && "Invoice", canTrack && "Tracking"].filter(Boolean).join(" & ");
  const roleBadge = isAdmin
    ? { icon: ShieldCheck, label: "Admin — full access", cls: "bg-emerald-50 text-emerald-700" }
    : { icon: Eye, label: extra ? `View only · ${extra} access` : "View only", cls: "bg-slate-100 text-slate-600" };
  const RoleIcon = roleBadge.icon;
  const badge = (
    <span className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${roleBadge.cls}`} data-testid="sc-role-badge">
      <RoleIcon className="w-3.5 h-3.5" /> {roleBadge.label}
    </span>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppNav badge={badge} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-[11px] uppercase tracking-[0.2em] text-accent font-num">Operations workspace</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-3">
          Supply chain &amp; procurement
        </h1>

        <div className="flex gap-1 mt-8 border-b border-slate-200 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sc-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors whitespace-nowrap ${
                  active === t.id ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {active === "allocation" && <AllocationPanel readOnly={!canAllocate} />}
          {active === "tracking" && <TrackingPanel readOnly={!canTrack} />}
          {active === "invoice" && <InvoiceAuditPanel readOnly={!canInvoice} />}
          {active === "services" && <ServicesPanel />}
          {active === "training" && <TrainingPanel />}
        </div>
      </div>
    </div>
  );
}
